/**
 * HTTP fetcher for API calls
 * Handles errors and normalizes responses
 */

export class HttpResponseError extends Error {
  readonly status: number;
  /** JSON `error` field when present (e.g. order validation code). */
  readonly errorCode?: string;

  constructor(params: { message: string; status: number; errorCode?: string }) {
    super(params.message);
    this.name = "HttpResponseError";
    this.status = params.status;
    this.errorCode = params.errorCode;
  }
}

export function isHttpResponseError(error: unknown): error is HttpResponseError {
  return error instanceof HttpResponseError;
}

export async function fetcher<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const text = await response.text();
    let parsed: { error?: unknown; message?: unknown } | null = null;
    try {
      parsed = JSON.parse(text) as { error?: unknown; message?: unknown };
    } catch {
      parsed = null;
    }
    const errorCode =
      parsed && typeof parsed.error === "string" && parsed.error.length > 0
        ? parsed.error
        : undefined;
    const detail =
      parsed && typeof parsed.message === "string" && parsed.message.length > 0
        ? parsed.message
        : undefined;
    const message = errorCode ?? detail ?? `HTTP error! status: ${response.status}`;
    throw new HttpResponseError({ message, status: response.status, errorCode });
  }

  return response.json() as Promise<T>;
}
